import { captureEvent, useCaptureEvent } from "@Light/app/analytics";
import { QueryLoader } from "@Light/components/app/QueryLoader";
import { Button } from "@Light/components/page/Button";
import { PageBody } from "@Light/components/page/PageBody";
import { useLight } from "@Light/services/light";
import type { Utility } from "@Light/services/lightTypes";
import { zip5 } from "@Light/utils/address";
import { Lightning01 } from "@untitledui/icons";
import { useState } from "react";
import { utilityAndPostalCodeToRequirementsPatch } from "../address/patch";
import { ServiceLocationSheets } from "../address/ServiceLocationSheets";
import { useServiceLocationSheets } from "../address/useServiceLocationSheets";
import { EnrollHelpButtonFooter } from "../EnrollHelpButtonFooter";
import { useEnrollment } from "../flow/context";
import { PartnerTosDisclaimer } from "../PartnerTosDisclaimer";
import { EnrollRoute } from "../routes";
import { useBrokerTermsAgreement } from "../useBrokerTermsAgreement";
import {
  makeUtilityChoiceSubtitle,
  OTHER_UTILITY,
  utilityChoiceHelpButtonText,
  UtilityRadioGroup,
  type UtilityChoice,
} from "./UtilityRadioGroup";
import { ZipUtilityIneligibleBottomSheet } from "./ZipUtilityIneligibleBottomSheet";

export const ZipUtilitySplit = () => {
  const { requirements, next, navigate } = useEnrollment();
  const { useCheckEligibilityQuery } = useLight();

  const existing = requirements.service_address?.value?.postal_code;
  const postalCode = existing ? zip5(existing) : undefined;
  const query = useCheckEligibilityQuery(
    { postal_code: postalCode ?? "" },
    { skip: !postalCode },
  );

  const utilities = query.data?.utilities;
  useCaptureEvent({
    event: "enroll_zip_split_viewed",
    condition: true,
    properties: {
      variant: "screen",
      postal_code: postalCode ?? null,
      utility_count: utilities?.length ?? 0,
    },
  });

  const [otherUtility, setOtherUtility] = useState(false);
  const brokerTermsAgreement = useBrokerTermsAgreement();
  const sheets = useServiceLocationSheets({
    postalCode,
    additionalRequirements: brokerTermsAgreement,
  });

  return (
    <QueryLoader
      queries={[query]}
      errorMessage="Error checking which utilities serve your area. Please try again later"
    >
      <BaseZipUtilitySplit
        postalCode={postalCode}
        utilities={utilities ?? []}
        onSubmit={(utility) => {
          if (!postalCode) return;
          captureEvent("enroll_zip_split_submitted", {
            variant: "screen",
            postal_code: postalCode,
            utility: utility.id,
          });
          void next({
            ...utilityAndPostalCodeToRequirementsPatch(
              requirements,
              postalCode,
              utility.id,
            ),
            ...brokerTermsAgreement,
          });
        }}
        onSelectOtherUtility={() => {
          if (!postalCode) return;
          captureEvent("enroll_zip_split_submitted", {
            variant: "screen",
            postal_code: postalCode,
            utility: OTHER_UTILITY,
          });
          setOtherUtility(true);
        }}
        onClickProvideAddress={() => {
          sheets.openSheet("address");
        }}
      />
      <ServiceLocationSheets {...sheets} />
      {postalCode ? (
        <ZipUtilityIneligibleBottomSheet
          open={otherUtility}
          postalCode={postalCode}
          reason="utility"
          onClose={() => {
            setOtherUtility(false);
          }}
          onRetry={() => {
            navigate(EnrollRoute.ZipUtilitySearch);
          }}
        />
      ) : null}
    </QueryLoader>
  );
};

export type BaseZipUtilitySplitProps = {
  postalCode?: string;
  utilities: Utility[];
  onSubmit: (utility: Utility) => void;
  onSelectOtherUtility: () => void;
  onClickProvideAddress: () => void;
};

export const BaseZipUtilitySplit = ({
  postalCode,
  utilities,
  onSubmit,
  onSelectOtherUtility,
  onClickProvideAddress,
}: BaseZipUtilitySplitProps) => {
  const [selected, setSelected] = useState<UtilityChoice>();

  return (
    <PageBody
      centered
      icon={Lightning01}
      title="Let’s find your electricity plan. Who is your local utility?"
      subtitle={makeUtilityChoiceSubtitle(postalCode, utilities)}
      footer={
        <div className="flex flex-col gap-3">
          <EnrollHelpButtonFooter
            helpButtonText={utilityChoiceHelpButtonText(utilities)}
          >
            <Button
              size="lg"
              className="w-full"
              disabled={!selected}
              onClick={() => {
                if (selected === OTHER_UTILITY) {
                  onSelectOtherUtility();
                } else if (selected) {
                  onSubmit(selected);
                }
              }}
              data-testid="zip-utility-split-submit"
            >
              View offer
            </Button>
          </EnrollHelpButtonFooter>
          <PartnerTosDisclaimer className="text-center" />
        </div>
      }
    >
      <UtilityRadioGroup
        name="zip-utility-split"
        utilities={utilities}
        value={selected}
        onChange={setSelected}
        onClickProvideAddress={onClickProvideAddress}
      />
    </PageBody>
  );
};
